import { create } from 'zustand';
import { createGuestSession, login, getProfile, validateToken } from '../api/auth';
import { notifications } from '@mantine/notifications';

interface User {
  id: string;
  username: string;
  role: 'administrator' | 'guest' | 'user' | 'member';
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

interface AuthActions {
  loginUser: (credentials: { username: string; password: string }) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setUser: (user: User) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>((set) => ({
  token: localStorage.getItem('token'),
  user: null,
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null,

  loginUser: async (credentials) => {
    set({ loading: true, error: null });
    try {
      const response = await login(credentials);
      
      localStorage.setItem('token', response.token);
      const profile = await getProfile();
  
      set({
        token: response.token,
        user: profile,
        isAuthenticated: true,
        loading: false,
      });
    } catch (error) {
      set({
        error: 'Login failed',
        loading: false,
      });
      notifications.show({
        title: 'Login Failed',
        message: 'Invalid credentials',
        color: 'red',
      });
      console.error('Login failed:', error);
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    set({
      token: null,
      user: null,
      isAuthenticated: false,
      error: null,
    });
    notifications.show({
      title: 'Logged Out',
      message: 'You have been logged out successfully',
      color: 'blue',
    });
  },

  checkAuth: async () => {
    const token = localStorage.getItem('token');
  
    if (!token) {
      try {
        set({ loading: true, error: null });
        const response = await createGuestSession();
        localStorage.setItem('token', response.token);
        set({ token: response.token, user: response.user, isAuthenticated: true, loading: false });
      } catch (error) {
        set({ error: 'Failed to authenticate as guest', loading: false });
        notifications.show({
          title: 'Authentication Error',
          message: 'Failed to authenticate as guest',
          color: 'red',
        });
      }
      return;
    }
  
    set({ loading: true });
    try {
      const user = await validateToken();
      set({
        user,
        isAuthenticated: true,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Invalid token. Logging out...');
      localStorage.removeItem('token');
      set({
        token: null,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: 'Session expired. Please log in again.',
      });
    }
  },

  setUser: (user) => {
    set({ user, isAuthenticated: true });
  },
}));
